import React, { memo, useCallback, useMemo, useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import GlassCard from '../card/GlassCard';
import { themeColors, rgbaColor } from 'helpers/utils';
import { defaultChartOptions } from 'services/glass-management/glassManagement';
import SLAChart from '../SLAChart';
import AppContext from 'context/Context';
import { useSelector, useDispatch } from 'react-redux';
import { openModal } from 'slices/glassDashboard/glassDataModalSlice';
import { otifCols, otifKeys, getRechargeValue } from 'services/glass-management/otif';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { Spinner } from 'react-bootstrap';

const Gradient = memo(({ colour, target = 95, percent = 0, jobs = [] }) => {
    const [loading, setLoading] = useState(false);
    const [recharge, setRecharge] = useState({});
    const { config: { isDark } } = useContext(AppContext);
    const background = useMemo(() => isDark ? themeColors.dark : themeColors.light, [isDark]);

    useEffect(() => {
        if ((jobs.length > 0 && percent > 0) && percent < target) {
            setLoading(true);
            getRechargeValue(target, percent, jobs)
                .then(res => setRecharge(res))
                .catch(err => toast.error('Error fetching recharge value', err))
                .finally(() => setLoading(false));
        }
    }, [percent, jobs]);

    return (
        <div
            className='position-absolute w-100 bottom-0 rounded'
            style={{
                height: '40px',
                background: `linear-gradient(3deg, ${rgbaColor(colour, .3)} -70%, ${rgbaColor(background)} 80%)`,
            }}
        >
            <small className='d-inline float-end text-500 pe-3 align-middle h-100'>Target: <b>{target}%</b></small>
            {jobs.length > 0 && <small
                title={`(Total Invoiced Value Of [£${recharge?.value?.toFixed(2) || '??'}]) X (% Below Target [${recharge?.pctBelowTarget?.toFixed(2) || '??'}]) X (Service Factor Of 25%)`}
                className='d-inline float-start text-500 ps-3 align-middle h-100'
            >
                Recharge value: {!loading ? <b>£{recharge?.recharge?.toFixed(2)}</b> : <Spinner className='align-middle' size='sm' variant='secondary' />}
            </small>}
        </div>
    );
});

const OTIF = () => {
    const dispatch = useDispatch();
    const { data, loading, error } = useSelector(s => s.otif);

    if (error)
        toast.error('Something went wrong!', error);

    const buildTableData = useCallback(data =>
        data.map(item => {
            return {
                ...item,
                OrderDate: dayjs(item.OrderDate).format('DD-MM-YYYY HH:mm:ss'),
                ScannedDate: dayjs(item.ScannedDate).format('DD-MM-YYYY HH:mm:ss'),
                RequiredDate: dayjs(item.RequiredDate).format('DD-MM-YYYY'),
                GlassRequiredDate: dayjs(item.GlassRequiredDate).format('DD-MM-YYYY'),
                IsOnTime: item.IsOnTime ? '✔' : '❌'
            }
        }), [data]);

    const onChartClick = useCallback((e, title) => {
        const key = e?.name?.replaceAll(' ', '');
        if (data?.raw?.hasOwnProperty(key)) {
            dispatch(openModal({
                modalData: buildTableData(data.raw[key]),
                modalLoading: false,
                modalShow: true,
                modalTitle: title + ' OTIF',
                tableCols: otifCols,
            }));
        }
    }, [dispatch, data]);

    const allDataClick = useCallback(keys => {
        const dataKeys = Object.keys(data?.raw).filter(x => keys.includes(x));
        dispatch(openModal({
            modalData: dataKeys.flatMap(x => buildTableData(data.raw[x])),
            modalLoading: false,
            modalShow: true,
            modalTitle: 'All OTIF',
            tableCols: otifCols,
        }))
    }, [data]);

    const getTargetClass = useCallback((percent, target = 95) => {
        let percentClass = 'text-success';
        let colour = themeColors.success;
        if (percent < target - 5) {
            percentClass = 'text-danger';
            colour = themeColors.danger;
        }
        else if (percent > target - 5 && percent < target) {
            percentClass = 'text-warning';
            colour = themeColors.warning;
        }
        return { percentClass, colour };
    }, []);

    const getOptions = useCallback((type, chartData) => {
        const lastColName = type?.includes('order') ? 'Out LeadTime' : 'Not Received';
        let xAxis = ['5+', '5', '4', '3', '2', '1', '0', lastColName];
        switch (type) {
            case 'remake':
                xAxis = ['5+', '5', '4', '3', '2', lastColName];
                break;
            case 'order':
                xAxis = ['5+', '5', '4', '3', lastColName];
                break;
        };

        return {
            ...defaultChartOptions,
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'shadow',
                },
                formatter: function (params) {
                    return `${params[1].name.split('-')[0]} days <br/> ${params[1].seriesName} <br /> ${params[1].value.toFixed(2)}%`;
                }
            },
            xAxis: {
                type: 'category',
                axisLabel: {
                    interval: 0,
                },
                data: xAxis,
            },
            series: [
                {
                    type: 'bar',
                    stack: 'Total',
                    label: {
                        show: false,
                    },
                    itemStyle: {
                        borderColor: 'transparent',
                        color: 'transparent'
                    },
                    emphasis: {
                        itemStyle: {
                            borderColor: 'transparent',
                            color: 'transparent'
                        }
                    },
                    data: chartData?.trans,
                },
                {
                    type: 'bar',
                    stack: 'Total',
                    color: [themeColors.success],
                    itemStyle: {
                        normal: {
                            label: {
                                color: themeColors.success,
                                fontSize: 15,
                                formatter: (e) => {
                                    return e.value > 0 ? e.value.toFixed(2) + '%' : '';
                                },
                                show: true,
                                position: 'top',
                            },
                        }
                    },
                    data: chartData?.pass,
                },
                {
                    type: 'bar',
                    stack: 'Total',
                    color: [themeColors.danger],
                    itemStyle: {
                        normal: {
                            label: {
                                color: themeColors.danger,
                                fontSize: 15,
                                distance: 25,
                                formatter: (e) => {
                                    return e.value > 0 ? e.value.toFixed(2) + '%' : '';
                                },
                                show: true,
                                position: 'top',
                            },
                        }
                    },
                    data: chartData?.fail,
                },
            ],
        }
    }, []);

    const actual = data?.datasets?.actual;
    const actualColours = getTargetClass(data?.datasets?.actual?.percent);
    const actualOptions = getOptions('actual', actual);

    const remake = data?.datasets?.remake;
    const remakeColours = getTargetClass(data?.datasets?.remake?.percent, 100);
    const remakeOptions = getOptions('remake', remake);

    const order = data?.datasets?.order;
    const orderColours = getTargetClass(data?.datasets?.order?.percent);
    const orderOptions = getOptions('order', order);

    const actualClick = useCallback((e) => onChartClick(e, 'Actual'), [onChartClick, data]);
    const remakeClick = useCallback((e) => onChartClick(e, 'Remake'), [onChartClick, data]);
    const orderClick = useCallback((e) => onChartClick(e, 'Order'), [onChartClick, data]);

    return (
        <>
            <GlassCard
                md={12}
                lg={12}
                heroValue={{ value: actual?.percent, class: actualColours?.percentClass, suffix: '%', decimals: 2, func: () => allDataClick(otifKeys.actual) }}
                title="Actual OTIF <small><b>(2.2)</b></small>">
                <SLAChart loading={loading} data={actual} options={actualOptions} onClick={actualClick} />
                <Gradient colour={actualColours.colour} jobs={actual?.jobs} percent={actual?.percent} />
            </GlassCard>
            <GlassCard
                md={12}
                lg={6}
                heroValue={{ value: remake?.percent, class: remakeColours?.percentClass, suffix: '%', decimals: 2, func: () => allDataClick(otifKeys.remake) }}
                title="Remake OTIF <small><b>(2.3)</b></small>">
                <SLAChart loading={loading} data={remake} options={remakeOptions} onClick={remakeClick} />
                <Gradient colour={remakeColours.colour} jobs={remake?.jobs} percent={remake?.percent} target={100} />
            </GlassCard>
            <GlassCard
                md={12}
                lg={6}
                heroValue={{ value: order?.percent, class: orderColours?.percentClass, suffix: '%', decimals: 2, func: () => allDataClick(otifKeys.order) }}
                title="Emplas Order Lead Times <small><b>(2.2)</b></small>">
                <SLAChart loading={loading} options={orderOptions} onClick={orderClick} />
                <Gradient colour={orderColours.colour} />
            </GlassCard>
        </>
    );
}

Gradient.propTypes = {
    colour: PropTypes.string,
    target: PropTypes.number,
    percent: PropTypes.number,
    jobs: PropTypes.array,
};
export default memo(OTIF);